import { Download, Printer } from 'lucide-react';
import { dateLabel, todayIso, type ResolvedPeriod } from './period';

/**
 * Ein gemeinsames Berichtsformat für alle Sektionen. Jede Sektion baut daraus ihre `SectionReport`;
 * daraus entstehen sowohl der CSV-Download als auch die druckfertige PDF-Ansicht (über den Browser).
 */
export interface ReportBlock {
  readonly heading: string;
  readonly columns: readonly string[];
  readonly rows: readonly (readonly (string | number)[])[];
}
export interface SectionReport {
  readonly title: string;
  readonly kpis: readonly { readonly label: string; readonly value: string }[];
  readonly blocks: readonly ReportBlock[];
}

function csvCell(value: string | number): string {
  const text = String(value);
  return /[";\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}
function slug(text: string): string {
  return text.toLowerCase().replace(/[äöü]/g, (c) => ({ ä: 'ae', ö: 'oe', ü: 'ue' }[c] ?? c)).replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}
function esc(value: string | number): string {
  return String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}
function download(blob: Blob, filename: string): void {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}

export function downloadReportCsv(report: SectionReport, periodLabel: string, tenantName: string): void {
  const rows: (string | number)[][] = [
    [`Auswertung: ${report.title}`],
    [tenantName, `Zeitraum: ${periodLabel}`, `Stand: ${dateLabel(todayIso())}`],
    [],
  ];
  if (report.kpis.length > 0) {
    rows.push(['Kennzahl', 'Wert']);
    for (const kpi of report.kpis) rows.push([kpi.label, kpi.value]);
    rows.push([]);
  }
  for (const block of report.blocks) {
    rows.push([block.heading]);
    rows.push([...block.columns]);
    for (const row of block.rows) rows.push([...row]);
    rows.push([]);
  }
  const csv = rows.map((row) => row.map(csvCell).join(';')).join('\r\n');
  download(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }), `auswertung-${slug(report.title)}-${todayIso()}.csv`);
}

/** Druckfertige HTML-Ansicht in einem neuen Tab -- der „PDF"-Weg läuft über „Drucken / als PDF speichern". */
export function openReportPrint(report: SectionReport, periodLabel: string, tenantName: string): void {
  const kpis = report.kpis.length
    ? `<div class="kpis">${report.kpis.map((kpi) => `<div class="kpi"><span>${esc(kpi.label)}</span><strong>${esc(kpi.value)}</strong></div>`).join('')}</div>`
    : '';
  const blocks = report.blocks.map((block) => `
    <h2>${esc(block.heading)}</h2>
    <table><thead><tr>${block.columns.map((column, index) => `<th class="${index === 0 ? '' : 'num'}">${esc(column)}</th>`).join('')}</tr></thead>
    <tbody>${block.rows.map((row) => `<tr>${row.map((cell, index) => `<td class="${index === 0 ? '' : 'num'}">${esc(cell)}</td>`).join('')}</tr>`).join('')}</tbody></table>`).join('');
  const html = `<!doctype html><html lang="de"><head><meta charset="utf-8"><title>${esc(report.title)} – ${esc(tenantName)}</title>
<style>
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: "Segoe UI", Arial, sans-serif; color: #1a1a1a; font-size: 12px; line-height: 1.45; background: #eee; }
  .toolbar { position: sticky; top: 0; display: flex; justify-content: center; padding: 8px; background: #222; }
  .toolbar button { font: inherit; font-size: 13px; padding: 7px 16px; border: 0; border-radius: 6px; cursor: pointer; background: #e30613; color: #fff; }
  .sheet { width: 210mm; margin: 12px auto; padding: 16mm; background: #fff; box-shadow: 0 1px 6px rgba(0,0,0,.18); }
  header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 3px solid #111; padding-bottom: 8px; margin-bottom: 6mm; }
  .brand { font-size: 20px; font-weight: 800; color: #e30613; }
  .rt { font-size: 18px; font-weight: 800; text-align: right; }
  .rp { font-size: 11px; color: #555; text-align: right; }
  .kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 6px; margin-bottom: 6mm; }
  .kpi { border: 1px solid #ddd; border-radius: 6px; padding: 8px 10px; }
  .kpi span { display: block; font-size: 8px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; color: #777; }
  .kpi strong { font-size: 15px; }
  h2 { font-size: 13px; margin: 7mm 0 2mm; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 3mm; }
  th, td { border: 1px solid #e2e2e2; padding: 4px 8px; text-align: left; font-size: 11px; }
  th { background: #fafafa; color: #0f6b5c; font-weight: 700; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
  footer { margin-top: 10mm; border-top: 1px solid #ccc; padding-top: 4px; font-size: 9px; color: #777; }
  @media print { body { background: #fff; } .toolbar { display: none; } .sheet { margin: 0; box-shadow: none; width: auto; padding: 0; } @page { size: A4; margin: 14mm; } h2, table { break-inside: avoid; } }
</style></head>
<body>
  <div class="toolbar"><button type="button" onclick="window.print()">Drucken / als PDF speichern</button></div>
  <div class="sheet">
    <header><div class="brand">${esc(tenantName)}</div><div><div class="rt">${esc(report.title)}</div><div class="rp">Zeitraum: ${esc(periodLabel)} · Stand ${esc(dateLabel(todayIso()))}</div></div></header>
    ${kpis}
    ${blocks}
    <footer>FORQ Auswertung · automatisch aus den aktuellen Daten erzeugt</footer>
  </div>
</body></html>`;
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  window.open(url, '_blank');
  // Der Blob bleibt bis zum Tab-Schließen gültig; kurz danach freigeben.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** CSV- + PDF-Knopf für die Kopfzeile einer Sektion. */
export function ReportActions({ report, period, tenantName }: {
  readonly report: SectionReport;
  readonly period: ResolvedPeriod;
  readonly tenantName: string;
}) {
  return (
    <div className="auswertung__actions">
      <button type="button" className="sales-secondary" onClick={() => downloadReportCsv(report, period.label, tenantName)}><Download size={15} /> CSV</button>
      <button type="button" className="sales-secondary" onClick={() => openReportPrint(report, period.label, tenantName)}><Printer size={15} /> PDF</button>
    </div>
  );
}
